import React, { useState } from 'react'
import { useBranding } from '../../context/BrandingContext';
import { PasswordHideIcon, PasswordLockSvg, PasswordShowIcon } from '../../assets/svg/SvgComponent';
import axios from "axios";
import { API_URL, getHeaders } from '../../helper/commonApi';

import "./ChangePasswordModal.css";
import toast from 'react-hot-toast';

const ChangePasswordModal = ({ onClose, userId }) => {
    const { colors } = useBranding();

    const [currentPass, setCurrentPass] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    const [showPass, setShowPass] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [showCurrent, setShowCurrent] = useState(false);

    const [currentError, setCurrentError] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [confirmError, setConfirmError] = useState("");

    const [generalError, setGeneralError] = useState("");
    const [loading, setLoading] = useState(false);


    const validateConfirmPassword = (value, passwordValue) => {
        setGeneralError("");
        if (!value) return "Confirm your password";
        if (value !== passwordValue) return "Passwords do not match";
        return "";
    };

    const validatePassword = (value) => {
        setGeneralError("");
        if (!value) return "Password is required";

        const minLength = /.{8,}/;
        const hasUpper = /[A-Z]/;
        const hasLower = /[a-z]/;
        const hasNumber = /\d/;
        const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/;

        if (!minLength.test(value)) return "Must be at least 8 characters";
        if (!hasUpper.test(value)) return "Must include at least one uppercase letter";
        if (!hasLower.test(value)) return "Must include at least one lowercase letter";
        if (!hasNumber.test(value)) return "Must include at least one number";
        if (!hasSpecial.test(value)) return "Must include at least one special character";

        return "";
    };

    const validateForm = () => {
        const currentErr = !currentPass ? "Current password is required" : "";
        let passErr = validatePassword(newPassword);
        const confirmErr = validateConfirmPassword(confirmPassword, newPassword);

        if (!currentErr && !passErr && currentPass === newPassword) {
            passErr = "New password cannot be the same as current password";
        }


        setCurrentError(currentErr);
        setPasswordError(passErr);
        setConfirmError(confirmErr);

        if (currentErr || passErr || confirmErr) return false;

        setGeneralError("");
        return true;
    };


    const handleSubmit = async (userData, passWithoutValidation = false) => {
        if (!passWithoutValidation) {
            if (!validateForm()) return;
        }
        setLoading(true);
        try {
            const res = await axios.post(`${API_URL}/change-password`, {
                user_id: userId,
                current_password: currentPass,
                new_password: newPassword
            }, { headers: getHeaders() });

            if (res.status === 200) {
                toast.success("Password changed successfully ")
                onClose();
            } else {
                setGeneralError(res?.data?.message || "Failed to change password");
            }
        } catch (err) {
            if (err?.response && err?.response.data) {
                setGeneralError(err?.response?.data?.message || "Something went wrong");
            } else {
                setGeneralError("Server error");
            }
        } finally {
            setLoading(false); // Hide loader
        }
    };

    return (
        <div className="popup-overlay">
            <div className="popup-box" style={{ background: colors.popupBg }}>
                <h2>Change Password</h2>

                {/* Close Icon */}
                <button className="popup-close-btn" onClick={onClose}>
                    <svg width="24px" height="24px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <rect width="24" height="24" fill="white"></rect> <path d="M7 17L16.8995 7.10051" stroke="#000000" stroke-linecap="round" stroke-linejoin="round"></path> <path d="M7 7.00001L16.8995 16.8995" stroke="#000000" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
                </button>

                <div className="input-wrapper">
                    <div className={`input-group ${currentError ? "error" : ""}`}>
                        <PasswordLockSvg />
                        <input
                            id="currentPassword"
                            name="currentPassword"
                            type={showCurrent ? "text" : "password"}
                            placeholder="Current Password"
                            value={currentPass}
                            onChange={(e) => {
                                const val = e.target.value;
                                setCurrentPass(val);
                                setCurrentError(validatePassword(val));
                            }
                            }
                        />
                        <div
                            className="input-type-password-toggle"
                            onClick={() => setShowCurrent(!showCurrent)}
                        >
                            {showCurrent ? <PasswordShowIcon /> : <PasswordHideIcon />}
                        </div>
                    </div>
                    {currentError && (
                        <span className="error-text">{currentError}</span>
                    )}
                </div>

                {/* Password */}
                <div className="input-wrapper">
                    <div className={`input-group ${passwordError ? "error" : ""}`}>
                        <PasswordLockSvg />
                        <input
                            id="newPassword"
                            name="newPassword"
                            type={showPass ? "text" : "password"}
                            placeholder="Password"
                            value={newPassword}
                            onChange={(e) => {
                                const val = e.target.value;
                                setNewPassword(val);
                                setPasswordError(validatePassword(val));
                                setConfirmError(validateConfirmPassword(confirmPassword, val));
                            }}
                            autoComplete="off"
                        />
                        <div
                            className="input-type-password-toggle"
                            onClick={() => setShowPass(!showPass)}
                        >
                            {showPass ? <PasswordShowIcon /> : <PasswordHideIcon />}
                        </div>
                    </div>
                    {passwordError && (
                        <span className="error-text">{passwordError}</span>
                    )}
                </div>

                {/* Confirm Password */}
                <div className="input-wrapper">
                    <div className={`input-group ${confirmError ? "error" : ""}`}>
                        <PasswordLockSvg />
                        <input
                            id="confirmNewPassword"
                            name="confirmNewPassword"
                            type={showConfirm ? "text" : "password"}
                            placeholder="Confirm Password"
                            value={confirmPassword}
                            onChange={(e) => {
                                const val = e.target.value;
                                setConfirmPassword(val);
                                setConfirmError(validateConfirmPassword(val, newPassword));
                            }}
                            autoComplete="off"
                        />
                        <div
                            className="input-type-password-toggle"
                            onClick={() => setShowConfirm(!showConfirm)}
                        >
                            {showConfirm ? <PasswordShowIcon /> : <PasswordHideIcon />}
                        </div>
                    </div>
                    {confirmError && (
                        <span className="error-text">{confirmError}</span>
                    )}
                </div>

                {generalError && <div className="verify-otp-error">{generalError}</div>}

                {/* Submit */}
                <div onClick={loading ? null : handleSubmit} className="register-btn btn black" style={{ backgroundColor: colors?.buttonBg }}>
                    {loading ? (
                        <span className="create-account-loader-btn" /> // or any spinner here
                    ) : (
                        "Chang Password"
                    )}
                </div>

            </div>
        </div>
    )
}

export default ChangePasswordModal